import { v4 as uuidv4 } from 'uuid';
import FeatureFlagModule, { FeatureFlagKeys } from '@modules/FeatureFlagModule';
import TasksModule from '@modules/TasksModule';
import { inboxActions, inboxState } from '../state';
import { isNewAssignedToMeUiEnabled } from './isNewAssignedToMeUiEnabled';
import {
  MANUAL_BOOKING_TASK_TYPE,
  OPEN_BOOK_JOB_ATTRIBUTE,
  isManualBookingTask,
  type OpenBookJobAttribute,
} from './manualBookings';

/** The flags behind the Assigned to Me gate (see `isNewAssignedToMeUiEnabled`). */
const GATE_FLAGS = [
  FeatureFlagKeys.featureAssignedToMeManualBookings,
  FeatureFlagKeys.featureEnableAgentInboxForCalls,
  FeatureFlagKeys.featureRetainEndedCallCards,
];

/** The customer, and the location, a manual booking starts for. */
export type ManualBookingPrefill = {
  customer: { id: string; name: string; orgNodeId: string };
  /** The location selected where the CSR clicked Book Job. */
  locationId?: string;
};

/**
 * Adds a manual booking card and selects it, as a CSR click. Without a
 * prefill its view opens customer search across all tenants; a selected
 * manual booking card with no customer yet is already that, so it stays
 * selected and no second card is added (a double click adds one card). With
 * a prefill, the card names that customer and its view opens Book Job for
 * them at once, at that location. Returns the card's task id.
 */
export function startManualBooking(prefill?: ManualBookingPrefill): string {
  const selected = TasksModule.getTask(inboxState.selectedTaskId ?? '');
  if (
    !prefill &&
    selected &&
    isManualBookingTask(selected) &&
    selected.attributes.customerId == null
  ) {
    return selected.id;
  }
  const id = `${MANUAL_BOOKING_TASK_TYPE}-${uuidv4()}`;
  const openBookJob: OpenBookJobAttribute | undefined = prefill
    ? { locationId: prefill.locationId ?? null }
    : undefined;
  TasksModule.createTask({
    id,
    type: MANUAL_BOOKING_TASK_TYPE,
    from: '',
    isStateActive: true,
    ...(openBookJob
      ? { attributes: { [OPEN_BOOK_JOB_ATTRIBUTE]: openBookJob } }
      : {}),
  });
  if (prefill) {
    TasksModule.setCustomerOnTask(id, prefill.customer);
  }
  inboxActions.selectTaskByUser(id);
  return id;
}

/**
 * Book Job on the Inbox home view, with the Assigned to Me gate on: adds a
 * manual booking card for that customer and location (or, without one, a
 * card on customer search) and selects it. Acts only while the home view
 * shows, so a repeated click adds no second card, and does nothing with the
 * gate off. Returns the new card's task id.
 */
export function startManualBookingFromHome(
  prefill?: ManualBookingPrefill,
): string | undefined {
  if (!isNewAssignedToMeUiEnabled()) {
    return undefined;
  }
  // InboxPage shows the home view unless the selected task is active
  const selectedId = inboxState.selectedTaskId;
  if (selectedId && TasksModule.getTask(selectedId)?.isStateActive) {
    return undefined;
  }
  return startManualBooking(prefill);
}

/**
 * Removes every manual booking card, discarding any booking on them, and
 * moves selection home if one was selected. Used when the Assigned to Me gate
 * turns off; sign-out and refresh clear the cards on their own.
 */
export function clearManualBookings(): void {
  const manualIds = TasksModule.getTasks()
    .filter(isManualBookingTask)
    .map((task) => task.id);
  if (manualIds.length === 0) {
    return;
  }
  // go home first, so the removed card is not recorded as the previous one
  if (manualIds.includes(inboxState.selectedTaskId ?? '')) {
    inboxActions.setHomeInbox();
  }
  manualIds.forEach((id) => {
    TasksModule.deleteTask(id);
    inboxActions.clearPreviousSelectedTaskId(id);
  });
}

/** True while any manual booking card is open, started or not. */
export function hasOpenManualBookings(): boolean {
  return TasksModule.getTasks().some(isManualBookingTask);
}

/**
 * Clears any open manual booking cards whenever the Assigned to Me gate turns
 * off. Registered once, at InboxModule preload.
 */
export function clearManualBookingsWhenGateTurnsOff(): void {
  GATE_FLAGS.forEach((flag) =>
    FeatureFlagModule.addFlagListener(flag, () => {
      if (!isNewAssignedToMeUiEnabled()) {
        clearManualBookings();
      }
    }),
  );
}
